import { useEffect, useState, useMemo } from "react";
import { useSearchParams } from "react-router-dom";
import { mask } from "remask";
import app from "../../../package.json";
//
import { formatDate } from "../../functions/formatDate";
import { getDateDiffText } from "../../functions/getDateDiffText";
import { toCurrency } from "../../functions/toCurrency";
//
// eslint-disable-next-line
import axios from "axios";
// eslint-disable-next-line
import apiPlay from "../../services/apiPlay";
//
import Base64Img from "../../components/Base64Img";
import LoadingCircle from "../../components/LoadingCircle";
import StatusFatura from "../../components/StatusFatura";
import TelaBoleto from "../../components/TelaBoleto";
import TelaCredito from "../../components/TelaCredito";
import TelaPix from "../../components/TelaPix";
import TelaRecorrencia from "../../components/TelaRecorrencia";
//
import { BiTransferAlt } from "react-icons/bi";
import {
  IoAlertCircle,
  IoBarcodeSharp,
  IoCardOutline,
  IoReload,
  IoChevronDown,
  IoChevronForward,
  IoPersonOutline,
  IoConstructOutline,
  IoCheckmarkCircle,
  IoReceiptOutline,
  IoDocumentTextOutline,
  IoSunnyOutline,
  IoMoonOutline,
} from "react-icons/io5";
//
import TermosDialog from "../../components/TermosDialog";
import styles from "./fatura.module.css";

// ── Parsing inteligente da descrição ──
// Detecta padrões como "(XX) XXXXX-XXXX - PLANO" e retorna lista estruturada
function parseDescription(desc) {
  if (!desc) return { type: "empty", lines: [] };
  const rawLines = desc.split("\n").map((l) => l.trim()).filter(Boolean);
  // Regex com grupos: captura telefone completo e plano separados pelo " - "
  const phoneRegex = /^(\(?\d{2}\)?\s?\d{4,5}-?\d{4})\s*[-–]\s*(.+)$/;
  const parsed = rawLines.map((line) => {
    const m = line.match(phoneRegex);
    if (m) {
      return { phone: m[1].trim(), plan: m[2].trim() };
    }
    return { text: line };
  });

  const hasPhones = parsed.some((p) => p.phone);
  if (hasPhones) return { type: "phones", lines: parsed };
  if (rawLines.length === 1) return { type: "simple", text: rawLines[0] };
  return { type: "multi", lines: rawLines };
}

export default function Fatura() {
  const [searchParams] = useSearchParams();
  const payId = searchParams.get("payid");

  const [formaPagamento, setFormaPagamento] = useState("0");
  const [loadingDados, setLoadingDados] = useState(true);
  const [fatura, setFatura] = useState({});
  const [isRecActive, setIsRecActive] = useState(false);
  const [cartoes, setCartoes] = useState([]);
  const [showOtherMethods, setShowOtherMethods] = useState(false);
  const [showDetails, setShowDetails] = useState(false);
  const [showDesc, setShowDesc] = useState(false);
  const [showDemoPanel, setShowDemoPanel] = useState(false);
  const [darkMode, setDarkMode] = useState(false);

  const toggleTheme = () => {
    setDarkMode((prev) => {
      const next = !prev;
      document.documentElement.setAttribute("data-theme", next ? "dark" : "light");
      return next;
    });
  };

  const isDemo = !payId;
  const isAdicional = fatura.payment === "ADICIONAL";

  const descData = useMemo(() => parseDescription(fatura.description), [fatura.description]);

  function isPaid() {
    return (
      fatura.status === "RECEIVED" ||
      fatura.status === "RECEIVED_IN_CASH" ||
      fatura.status === "CONFIRMED"
    );
  }

  function isOverdue() {
    return fatura.status === "OVERDUE";
  }

  function isRefunded() {
    return fatura.status === "REFUNDED" || fatura.status === "REFUND_REQUESTED";
  }

  function getContainerClass() {
    if (isPaid()) return styles["container-paid"];
    if (isOverdue()) return styles["container-overdue"];
    if (isRefunded()) return styles["container-refunded"];
    if (fatura.status === "PENDING") return styles["container-pending"];
    return styles["container"];
  }

  function getStatusClass() {
    if (isPaid()) return styles["status-paid"];
    if (isOverdue()) return styles["status-overdue"];
    if (isRefunded()) return styles["status-refunded"];
    return styles["status-pending"];
  }

  useEffect(() => {
    console.log(`Fatura v.${app.version} (modelo demonstrativo)`);
    const faturaDemo = {
      status: "PENDING",
      value: 615.00,
      dueDate: new Date(Date.now() + 5 * 24 * 60 * 60 * 1000).toISOString().split("T")[0],
      description: "(35) 92003-8028 - ICON UNIVERSITÁRIO\n(35) 99812-5541 - CONTROLE 25GB\n(35) 98804-7712 - CONTROLE 15GB\n(35) 99148-3390 - CONTROLE 15GB\n(35) 99761-0824 - CONTROLE 15GB\n(62) 99445-1182 - CONTROLE 25GB",
      contafatura: true,
      nome: "MGF Sul Construtora e Serviços Ltda",
      cpf: "10617255000151",
      email: "mgfsul@hotmail.com",
      msisdn: "5535920038028",
      plandescription: "ICON UNIVERSITÁRIO",
      payment: "MENSAL",
      nomeempresa: "Easy Brasil Telecom",
      cnpj: "43470720000123",
      companyid: "demo-001",
      logo: null,
      encodedimage: null,
      payload: "00020126580014br.gov.bcb.pix0136a1b2c3d4-e5f6-7890-abcd-ef1234567890520400005303986540615.005802BR5925EASY BRASIL TELECOM LTDA6008BRASILIA62070503***6304DEMO",
      link: "https://www.asaas.com/b/pdf/demo/demo123",
      codigoboleto: "23793.38128 60000.000003 00000.000402 1 00000000061500",
      barcode: "23791000000006150033812860000000000000000040",
      paymentDate: null,
      billingType: null,
    };
    setFatura(faturaDemo);
    setCartoes([]);
    setIsRecActive(false);
    setLoadingDados(false);
    // eslint-disable-next-line
  }, []);

  if (loadingDados) {
    return (
      <div className={styles["loading"]}>
        <LoadingCircle />
      </div>
    );
  }

  const renderPaymentContent = () => {
    const key = formaPagamento;
    return (
      <div className={styles["payment-content"]} key={key}>
        {key === "0" && <TelaPix fatura={fatura} />}
        {key === "1" && <TelaBoleto fatura={fatura} />}
        {key === "2" && <TelaCredito fatura={fatura} payId={payId} />}
        {key === "3" && (
          <TelaRecorrencia
            fatura={fatura}
            payId={payId}
            cartoesInitial={cartoes}
            isRecActive={isRecActive}
            onToggleRec={setIsRecActive}
          />
        )}
      </div>
    );
  };

  // ── Renderiza descrição inteligente ──
  const renderDescription = () => {
    if (!fatura.contafatura || !fatura.description) return null;

    if (descData.type === "simple") {
      return <span className={styles["desc-simple"]}>{descData.text}</span>;
    }

    if (descData.type === "phones") {
      const phoneLines = descData.lines.filter((l) => l.phone);
      const count = phoneLines.length;

      // 1 linha: mostra direto sem toggle
      if (count === 1) {
        const line = phoneLines[0];
        return (
          <div className={styles["desc-section"]}>
            <div className={styles["desc-lines"]}>
              <div className={styles["desc-line-item"]}>
                <span className={styles["desc-line-phone"]}>{line.phone}</span>
                <span className={styles["desc-line-plan"]}>{line.plan}</span>
              </div>
            </div>
          </div>
        );
      }

      // Múltiplas linhas: toggle colapsável
      return (
        <div className={styles["desc-section"]}>
          <button
            className={styles["desc-toggle"]}
            onClick={() => setShowDesc((p) => !p)}
            type="button"
          >
            <span className={styles["desc-toggle-count"]}>{count} linhas</span>
            <span>inclusas nesta fatura</span>
            <IoChevronDown
              className={`${styles["desc-chevron"]} ${showDesc ? styles["desc-chevron-open"] : ""}`}
            />
          </button>
          {showDesc && (
            <div className={styles["desc-lines"]}>
              {descData.lines.map((item, i) =>
                item.phone ? (
                  <div key={i} className={styles["desc-line-item"]}>
                    <span className={styles["desc-line-phone"]}>{item.phone}</span>
                    <span className={styles["desc-line-plan"]}>{item.plan}</span>
                  </div>
                ) : (
                  <div key={i} className={styles["desc-line-item"]}>
                    <span className={styles["desc-line-plan"]}>{item.text}</span>
                  </div>
                )
              )}
            </div>
          )}
        </div>
      );
    }

    // multi-line fallback
    if (descData.type === "multi") {
      return (
        <div className={styles["desc-section"]}>
          <button
            className={styles["desc-toggle"]}
            onClick={() => setShowDesc((p) => !p)}
            type="button"
          >
            <span>Detalhes da fatura</span>
            <IoChevronDown
              className={`${styles["desc-chevron"]} ${showDesc ? styles["desc-chevron-open"] : ""}`}
            />
          </button>
          {showDesc && (
            <div className={styles["desc-lines"]}>
              {descData.lines.map((line, i) => (
                <div key={i} className={styles["desc-line-item"]}>
                  <span className={styles["desc-line-plan"]}>{line}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      );
    }

    return null;
  };

  return (
    <div className={getContainerClass()}>
      <div className={styles["fatura"]}>

        {/* ─── Top Bar ─── */}
        <header className={styles["topbar"]}>
          <div className={styles["topbar-left"]}>
            {fatura.logo && (
              <div className={styles["topbar-logo"]}>
                <Base64Img data={fatura.logo} className={styles["topbar-logo-img"]} />
              </div>
            )}
            <span className={styles["topbar-name"]}>
              {fatura.nomeempresa && fatura.nomeempresa.toUpperCase()}
            </span>
          </div>
          <a
            className={styles["topbar-help"]}
            href={`https://atendimento.operadora.app.br/?companyId=${fatura.companyid}`}
            target='_blank'
            rel='noopener noreferrer'
          >
            Ajuda
          </a>
        </header>

        {/* ─── Hero: Valor ─── */}
        <div className={styles["hero"]}>
          <div className={getStatusClass()}>
            <StatusFatura fatura={fatura} />
          </div>

          <div className={styles["hero-amount"]}>
            <span className={styles["hero-currency"]}>R$</span>
            <span className={styles["hero-value"]}>
              {fatura.value ? toCurrency(fatura.value).replace(/R\$\s?/, "") : "0,00"}
            </span>
          </div>

          <div className={styles["hero-meta"]}>
            <span className={styles["hero-due"]}>
              Venc. {fatura.dueDate
                ? formatDate(`${fatura.dueDate} 00:00:00`.replace(/-/g, "/"))
                : "—"}
            </span>
          </div>

          {fatura.dueDate && !isPaid() && (
            <span className={styles["hero-countdown"]}>
              {getDateDiffText(fatura.dueDate + " 00:00:00")}
            </span>
          )}

          {renderDescription()}
        </div>

        {/* ─── Comprovante (quando pago) ─── */}
        {isPaid() && (
          <div className={styles["receipt-section"]}>
            <div className={styles["receipt-grid"]}>
              {fatura.paymentDate && (
                <div className={styles["receipt-item"]}>
                  <IoCheckmarkCircle className={styles["receipt-item-icon"]} style={{ color: "var(--color-success)" }} />
                  <div className={styles["receipt-item-info"]}>
                    <span className={styles["receipt-item-label"]}>Pago em</span>
                    <span className={styles["receipt-item-value"]}>
                      {formatDate(`${fatura.paymentDate} 00:00:00`.replace(/-/g, "/"))}
                    </span>
                  </div>
                </div>
              )}
              {fatura.billingType && (
                <div className={styles["receipt-item"]}>
                  <IoReceiptOutline className={styles["receipt-item-icon"]} />
                  <div className={styles["receipt-item-info"]}>
                    <span className={styles["receipt-item-label"]}>Forma</span>
                    <span className={styles["receipt-item-value"]}>{fatura.billingType}</span>
                  </div>
                </div>
              )}
            </div>
            <a
              className={styles["receipt-nota"]}
              href={`https://portaldenotas.operadora.app.br/?cpf=${fatura.cpf}`}
              target="_blank"
              rel="noopener noreferrer"
            >
              <IoDocumentTextOutline />
              <span>Consultar nota fiscal</span>
              <IoChevronForward className={styles["receipt-nota-arrow"]} />
            </a>
          </div>
        )}

        {/* ─── Detalhes do cliente (colapsável) ─── */}
        <button
          className={styles["details-toggle"]}
          onClick={() => setShowDetails((p) => !p)}
          type="button"
        >
          <IoPersonOutline className={styles["details-toggle-icon"]} />
          <div className={styles["details-toggle-info"]}>
            <span className={styles["details-toggle-name"]}>{fatura.nome || "Cliente"}</span>
            {fatura.msisdn && (
              <span className={styles["details-toggle-phone"]}>
                {mask(fatura.msisdn.slice(2), "(99) 99999-9999")}
              </span>
            )}
          </div>
          <IoChevronDown
            className={`${styles["details-chevron"]} ${showDetails ? styles["details-chevron-open"] : ""}`}
          />
        </button>

        {showDetails && (
          <div className={styles["details-panel"]}>
            <div className={styles["detail-row"]}>
              <span className={styles["detail-label"]}>CPF/CNPJ</span>
              <span className={styles["detail-value"]}>
                {fatura.cpf
                  ? mask(fatura.cpf, ["999.999.999-99", "99.999.999/9999-99"])
                  : "-"}
              </span>
            </div>
            <div className={styles["detail-row"]}>
              <span className={styles["detail-label"]}>
                {fatura.payment === "ADICIONAL" ? "Descrição" : "Plano"}
              </span>
              <span className={styles["detail-value"]}>
                {fatura.payment === "ADICIONAL"
                  ? (fatura.description || "-")
                  : (fatura.plandescription || "-")}
              </span>
            </div>
            {fatura.email && (
              <div className={styles["detail-row"]}>
                <span className={styles["detail-label"]}>E-mail</span>
                <span className={styles["detail-value-muted"]}>{fatura.email}</span>
              </div>
            )}
          </div>
        )}

        {/* ─── Pagamento ─── */}
        {!isPaid() && (
          <div className={styles["payment-section"]}>

            {/* Termos */}
            {!isAdicional && (
              <div className={styles["termos-notice"]}>
                <span className={styles["termos-text"]}>
                  Ao pagar, você concorda com os{" "}
                  <TermosDialog nomeMvno={fatura.nomeempresa}>
                    <span className={styles["termos-link"]}>termos e condições</span>
                  </TermosDialog>
                </span>
                <IoChevronDown className={styles["termos-arrow"]} />
              </div>
            )}

            {/* PIX direto */}
            {formaPagamento === "0" && renderPaymentContent()}

            {/* Outros métodos — conteúdo */}
            {formaPagamento !== "0" && (
              <>
                {formaPagamento === '3' && (
                  <div className={styles['alert-recurrence']}>
                    <IoAlertCircle size={16} color="var(--color-warning)" />
                    <p>Cobrança no <strong>primeiro cartão</strong>. Se falhar, tentamos o <strong>segundo</strong> e <strong>terceiro</strong>.</p>
                  </div>
                )}
                {renderPaymentContent()}
              </>
            )}

            {/* Outras formas (quando em PIX) */}
            {formaPagamento === "0" && (
              <div className={styles["alt-methods"]}>
                <button
                  className={styles["alt-methods-toggle"]}
                  onClick={() => setShowOtherMethods((p) => !p)}
                  type="button"
                >
                  <span>Outras formas de pagamento</span>
                  <IoChevronDown
                    className={`${styles["alt-chevron"]} ${showOtherMethods ? styles["alt-chevron-open"] : ""}`}
                  />
                </button>

                {showOtherMethods && (
                  <div className={styles["alt-methods-list"]}>
                    <button
                      className={styles["alt-option"]}
                      onClick={() => { setFormaPagamento("2"); setShowOtherMethods(false); }}
                      type="button"
                    >
                      <IoCardOutline className={styles["alt-option-icon"]} />
                      <div className={styles["alt-option-info"]}>
                        <span className={styles["alt-option-title"]}>Cartão de crédito</span>
                        <span className={styles["alt-option-desc"]}>Aprovação imediata</span>
                      </div>
                      <IoChevronForward className={styles["alt-option-arrow"]} />
                    </button>

                    {fatura.msisdn && (
                      <button
                        className={styles["alt-option"]}
                        onClick={() => { setFormaPagamento("3"); setShowOtherMethods(false); }}
                        type="button"
                      >
                        <IoReload className={styles["alt-option-icon"]} />
                        <div className={styles["alt-option-info"]}>
                          <span className={styles["alt-option-title"]}>Cobrança recorrente</span>
                          <span className={styles["alt-option-desc"]}>Débito mensal no cartão</span>
                        </div>
                        <IoChevronForward className={styles["alt-option-arrow"]} />
                      </button>
                    )}

                    <button
                      className={styles["alt-option"]}
                      onClick={() => { setFormaPagamento("1"); setShowOtherMethods(false); }}
                      type="button"
                    >
                      <IoBarcodeSharp className={styles["alt-option-icon"]} />
                      <div className={styles["alt-option-info"]}>
                        <span className={styles["alt-option-title"]}>Boleto bancário</span>
                        <span className={styles["alt-option-desc"]}>Até 3 dias para compensar</span>
                      </div>
                      <IoChevronForward className={styles["alt-option-arrow"]} />
                    </button>
                  </div>
                )}
              </div>
            )}

            {/* Voltar ao PIX */}
            {formaPagamento !== "0" && (
              <button
                className={styles["back-pix"]}
                onClick={() => setFormaPagamento("0")}
                type="button"
              >
                <BiTransferAlt />
                <span>Pagar com PIX</span>
              </button>
            )}
          </div>
        )}

        {/* ─── Footer ─── */}
        <footer className={styles["footer"]}>
          <span className={styles["footer-text"]}>
            {fatura.nomeempresa?.toUpperCase()} | CNPJ{" "}
            {fatura.cnpj && mask(fatura.cnpj, ["99.999.999/9999-99"])}
          </span>
        </footer>

      </div>

      {/* ─── Demo Panel (só quando sem payId) ─── */}
      {isDemo && (
        <>
          <button
            className={styles["demo-fab"]}
            onClick={() => setShowDemoPanel((p) => !p)}
            type="button"
            title="Painel de demonstração"
          >
            <IoConstructOutline />
          </button>

          {showDemoPanel && (
            <div className={styles["demo-panel"]}>
              <span className={styles["demo-panel-title"]}>Modo Demonstração</span>
              <div className={styles["demo-panel-group"]}>
                <span className={styles["demo-panel-label"]}>Tema</span>
                <div className={styles["demo-panel-buttons"]}>
                  <button
                    className={`${styles["demo-btn"]} ${!darkMode ? styles["demo-btn-active"] : ""}`}
                    style={!darkMode ? { borderColor: "#ff9500", color: "#ff9500" } : {}}
                    onClick={() => { setDarkMode(false); document.documentElement.setAttribute("data-theme", "light"); }}
                    type="button"
                  >
                    <IoSunnyOutline style={{ marginRight: 4 }} /> Claro
                  </button>
                  <button
                    className={`${styles["demo-btn"]} ${darkMode ? styles["demo-btn-active"] : ""}`}
                    style={darkMode ? { borderColor: "#007aff", color: "#007aff" } : {}}
                    onClick={() => { setDarkMode(true); document.documentElement.setAttribute("data-theme", "dark"); }}
                    type="button"
                  >
                    <IoMoonOutline style={{ marginRight: 4 }} /> Escuro
                  </button>
                </div>
              </div>
              <div className={styles["demo-panel-group"]}>
                <span className={styles["demo-panel-label"]}>Status</span>
                <div className={styles["demo-panel-buttons"]}>
                  {[
                    { status: "PENDING", label: "Pendente", color: "#ff9500" },
                    { status: "OVERDUE", label: "Vencida", color: "#ff3b30" },
                    { status: "CONFIRMED", label: "Paga", color: "#34c759" },
                    { status: "REFUNDED", label: "Estornada", color: "#007aff" },
                  ].map((item) => (
                    <button
                      key={item.status}
                      className={`${styles["demo-btn"]} ${fatura.status === item.status ? styles["demo-btn-active"] : ""}`}
                      style={fatura.status === item.status ? { borderColor: item.color, color: item.color } : {}}
                      onClick={() => {
                        const isPaidStatus = item.status === "CONFIRMED" || item.status === "RECEIVED";
                        setFatura((f) => ({
                          ...f,
                          status: item.status,
                          paymentDate: isPaidStatus ? new Date(Date.now() - 2 * 24 * 60 * 60 * 1000).toISOString().split("T")[0] : null,
                          billingType: isPaidStatus ? "PIX" : null,
                        }));
                      }}
                      type="button"
                    >
                      {item.label}
                    </button>
                  ))}
                </div>
              </div>
              <div className={styles["demo-panel-group"]}>
                <span className={styles["demo-panel-label"]}>Cenário</span>
                <div className={styles["demo-panel-buttons"]}>
                  <button
                    className={`${styles["demo-btn"]} ${fatura.payment === "MENSAL" && fatura.cpf?.length > 11 ? styles["demo-btn-active"] : ""}`}
                    onClick={() => setFatura((f) => ({
                      ...f,
                      payment: "MENSAL",
                      contafatura: true,
                      description: "(35) 92003-8028 - ICON UNIVERSITÁRIO\n(35) 99812-5541 - CONTROLE 25GB\n(35) 98804-7712 - CONTROLE 15GB\n(35) 99148-3390 - CONTROLE 15GB\n(35) 99761-0824 - CONTROLE 15GB\n(62) 99445-1182 - CONTROLE 25GB",
                      value: 615.00,
                      nome: "MGF Sul Construtora e Serviços Ltda",
                      cpf: "10617255000151",
                      email: "mgfsul@hotmail.com",
                      msisdn: "5535920038028",
                      plandescription: "ICON UNIVERSITÁRIO",
                    }))}
                    type="button"
                  >
                    PJ (6 linhas)
                  </button>
                  <button
                    className={`${styles["demo-btn"]} ${fatura.payment === "MENSAL" && fatura.cpf?.length <= 11 ? styles["demo-btn-active"] : ""}`}
                    onClick={() => setFatura((f) => ({
                      ...f,
                      payment: "MENSAL",
                      contafatura: true,
                      description: "(61) 99988-4455 - CONTROLE 25GB",
                      value: 59.90,
                      nome: "Maria da Silva Santos",
                      cpf: "12345678901",
                      email: "maria.santos@email.com",
                      msisdn: "5561999884455",
                      plandescription: "CONTROLE 25GB",
                    }))}
                    type="button"
                  >
                    PF (1 linha)
                  </button>
                  <button
                    className={`${styles["demo-btn"]} ${fatura.payment === "ADICIONAL" ? styles["demo-btn-active"] : ""}`}
                    onClick={() => setFatura((f) => ({
                      ...f,
                      payment: "ADICIONAL",
                      contafatura: true,
                      description: "Recarga avulsa - Crédito R$ 30,00",
                      value: 30.00,
                      nome: "João Pedro Oliveira",
                      cpf: "98765432100",
                      email: "joao.pedro@gmail.com",
                      msisdn: "5561998765432",
                      plandescription: "Controle 15GB",
                    }))}
                    type="button"
                  >
                    Adicional
                  </button>
                </div>
              </div>
            </div>
          )}
        </>
      )}

    </div>
  );
}
